import React, { useEffect, useMemo, useState } from 'react';

// Scrub previews + scene markers for our custom progress bars. Both only show
// up when the story is streaming from Mux (playback != null).

// Keyed by playback ID (URLs carry a fresh token every load); a few entries.
const storyboardCache = new Map();
const CACHE_MAX = 12;
const remember = (key, value) => {
  storyboardCache.delete(key);
  storyboardCache.set(key, value);
  while (storyboardCache.size > CACHE_MAX) storyboardCache.delete(storyboardCache.keys().next().value);
};

/** Fetch storyboard.json once per playback: {tile_width, tile_height, tiles:[{start,x,y}]} */
export function useStoryboard(playback) {
  const url = playback?.storyboard_url;
  const key = playback?.playback_id || url;
  const [board, setBoard] = useState(() => (key ? storyboardCache.get(key) || null : null));

  useEffect(() => {
    if (!url) {
      setBoard(null);
      return undefined;
    }
    if (storyboardCache.has(key)) {
      setBoard(storyboardCache.get(key));
      return undefined;
    }
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled || !json || !Array.isArray(json.tiles) || !json.tiles.length) return;
        const value = {
          tileWidth: json.tile_width,
          tileHeight: json.tile_height,
          tiles: [...json.tiles].sort((a, b) => a.start - b.start),
          cols: Math.round(Math.max(...json.tiles.map((t) => t.x)) / json.tile_width) + 1,
          rows: Math.round(Math.max(...json.tiles.map((t) => t.y)) / json.tile_height) + 1,
          // The sprite needs its own signed token; the JSON's `url` has none.
          image: playback.storyboard_image_url || json.url,
        };
        remember(key, value);
        setBoard(value);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return board;
}

const tileAt = (board, time) => {
  let pick = board.tiles[0];
  for (const tile of board.tiles) {
    if (tile.start <= time) pick = tile;
    else break;
  }
  return pick;
};

const sceneAt = (scenes, time) => {
  let pick = null;
  for (const scene of scenes || []) {
    if (scene.start <= time) pick = scene;
    else break;
  }
  return pick;
};

const formatTime = (t) => {
  if (!Number.isFinite(t) || t < 0) return '0:00';
  return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
};

/** Hover state for a progress bar element: spread onto the bar. */
export function useProgressHover(duration) {
  const [hover, setHover] = useState(null); // {ratio, time}
  const handlers = useMemo(
    () => ({
      onMouseMove: (e) => {
        if (!duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        setHover({ ratio, time: ratio * duration });
      },
      onMouseLeave: () => setHover(null),
    }),
    [duration]
  );
  return [hover, handlers];
}

/**
 * Tooltip above the bar at the hover point: storyboard frame (if any), scene
 * title (if any) and the time. Render inside a position:relative bar.
 */
export function ScrubPreview({ hover, board, scenes, width = 160 }) {
  if (!hover) return null;
  const scene = sceneAt(scenes, hover.time);
  const tile = board ? tileAt(board, hover.time) : null;
  const scale = board ? width / board.tileWidth : 1;
  const height = board ? Math.round(board.tileHeight * scale) : 0;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 30px)',
        left: `${hover.ratio * 100}%`,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {tile && (
        <div
          style={{
            position: 'relative',
            padding: 5,
            background: '#FFFFFF',
            borderRadius: 14,
            boxShadow: '0 10px 26px rgba(11,26,51,0.28)',
            transform: 'rotate(-2deg)',
          }}
        >
          {/* washi tape, like the Up next card */}
          <span style={{ position: 'absolute', top: -8, left: '50%', width: 46, height: 15, marginLeft: -23, background: 'rgba(255,215,106,0.88)', transform: 'rotate(-6deg)', borderRadius: 3, zIndex: 1 }} />
          <div
            style={{
              width,
              height,
              borderRadius: 10,
              backgroundColor: '#EAF3FF',
              backgroundImage: `url("${board.image}")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: `-${tile.x * scale}px -${tile.y * scale}px`,
              backgroundSize: `${board.cols * board.tileWidth * scale}px ${board.rows * board.tileHeight * scale}px`,
            }}
          />
        </div>
      )}
      <div
        style={{
          background: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: '#0B1A33',
          boxShadow: '0 4px 14px rgba(11,26,51,0.18)',
          fontFamily: "'Fredoka', 'Inter', sans-serif",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.2,
          padding: '4px 9px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {scene?.title ? `${scene.title} · ` : ''}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(hover.time)}</span>
      </div>
    </div>
  );
}

/** Small ticks where each AI-detected scene starts. Click jumps there. */
export function SceneMarkers({ scenes, duration, onSeek }) {
  if (!duration || !scenes?.length) return null;
  return scenes
    .filter((s) => s.start > 0.5 && s.start < duration - 0.5)
    .map((s) => (
      <button
        key={`${s.start}-${s.title}`}
        type="button"
        aria-label={s.title ? `Jump to ${s.title}` : `Jump to ${formatTime(s.start)}`}
        title={s.title || undefined}
        onClick={(e) => {
          e.stopPropagation();
          onSeek?.(s.start);
        }}
        style={{
          position: 'absolute',
          top: '50%',
          left: `${(s.start / duration) * 100}%`,
          width: 4,
          height: 'calc(100% + 4px)',
          minHeight: 8,
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255,255,255,0.95)',
          border: 'none',
          borderRadius: 2,
          padding: 0,
          cursor: 'pointer',
          zIndex: 5,
        }}
      />
    ));
}
